import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import {
  type DashboardStats,
  type SubThemeSentimentBreakdown,
  type ThemeSentimentBreakdown,
} from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';
import { SENTIMENT_ANALYSIS_CONFIG } from '../../sentiment-analysis.config';

export type SentimentFilterValue = 'all' | 'positive' | 'neutral' | 'negative';
export type ViewModeValue = 'themes' | 'subthemes';

/** Collapsible "Others" for theme-wise list (all suggested themes in one block). */
export const THEMES_OTHERS_SECTION_ID = '_section_themes_suggested_others_';

/** Collapsible "Others" for sub-theme-wise list — suggested themes (`is_suggested_theme`) at the bottom. */
export const SUBTHEMES_OTHERS_SECTION_ID = '_section_subthemes_suggested_others_';

export interface SubThemeRow {
  subThemeId: string;
  subThemeName: string;
  count: number;
  key: string;
}

export interface GroupedSubThemeGroup {
  themeId: string;
  themeName: string;
  total: number;
  /** Under non–suggested themes: rows with `is_suggested_subtheme` false. */
  subThemes: SubThemeRow[];
  /** Under non–suggested themes only: `is_suggested_theme` false and `is_suggested_subtheme` true. */
  othersInTheme: { total: number; rows: SubThemeRow[] } | null;
}

@Component({
  selector: 'app-sentiment-theme-distribution-widget',
  standalone: false,
  templateUrl: './theme-distribution-widget.component.html',
  styleUrls: ['./theme-distribution-widget.component.scss'],
})
export class SentimentThemeDistributionWidgetComponent implements OnInit, OnChanges {
  @Input() stats: DashboardStats | null = null;
  @Input() sentimentFilter: SentimentFilterValue = 'all';
  @Input() viewMode: ViewModeValue = 'themes';
  @Output() sentimentFilterChange = new EventEmitter<SentimentFilterValue>();
  @Output() viewModeChange = new EventEmitter<ViewModeValue>();

  readonly sentimentOptions = SENTIMENT_ANALYSIS_CONFIG.sentimentOptions;
  readonly viewOptions: { value: ViewModeValue; label: string }[] = [
    { value: 'themes', label: 'Themes' },
    { value: 'subthemes', label: 'Sub-themes' },
  ];

  private collapsedThemeIds = new Set<string>();
  /** Collapse state for theme groups inside Sub-theme → Others (keys `others-inner::{themeId}`). */
  private collapsedSubthemeOthersInnerThemeIds = new Set<string>();
  /** Suggested "Others" blocks start collapsed; toggled by section id. */
  private othersSectionExpandedIds = new Set<string>();

  /** Cached – updated only in ngOnChanges to avoid getters running every change detection. */
  distribution: Array<{ id: string; name: string; count: number }> = [];
  /** Theme view: non-suggested rows (flat list). */
  themeMainRows: Array<{ id: string; name: string; count: number }> = [];
  /** Theme view: collapsible Others with suggested theme rows inside. */
  themeOthersSection: { total: number; rows: Array<{ id: string; name: string; count: number }> } | null = null;
  groupedSubThemes: GroupedSubThemeGroup[] = [];
  /** Sub-theme view: global Others — rows with `is_suggested_theme` true (suggested themes), grouped by theme. */
  subthemeOthersSection: { total: number; themeGroups: GroupedSubThemeGroup[] } | null = null;
  maxCount = 1;
  maxSubThemeCount = 1;
  listTitle = 'Theme-wise distribution';

  ngOnInit(): void {
    this.updateCache();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['stats'] ||
      changes['sentimentFilter'] ||
      changes['viewMode']
    ) {
      this.updateCache();
    }
  }

  private updateCache(): void {
    try {
      this.listTitle =
        this.viewMode === 'themes' ? 'Theme-wise distribution' : 'Sub-theme-wise distribution';
      const s = this.stats;
      if (!s) {
        this.distribution = [];
        this.themeMainRows = [];
        this.themeOthersSection = null;
        this.groupedSubThemes = [];
        this.subthemeOthersSection = null;
        this.collapsedThemeIds.clear();
        this.collapsedSubthemeOthersInnerThemeIds.clear();
        this.maxCount = 1;
        this.maxSubThemeCount = 1;
        return;
      }
      if (this.viewMode === 'themes') {
        this.othersSectionExpandedIds.clear();
        this.collapsedThemeIds.clear();
        const tv = this.buildThemeDistributionView(s);
        this.themeMainRows = tv.mainRows;
        this.themeOthersSection = tv.othersSection;
        this.distribution = [];
        this.maxCount = tv.maxBarCount;
        this.groupedSubThemes = [];
        this.subthemeOthersSection = null;
        this.collapsedSubthemeOthersInnerThemeIds.clear();
        this.maxSubThemeCount = 1;
      } else {
        this.othersSectionExpandedIds.clear();
        this.distribution = [];
        this.themeMainRows = [];
        this.themeOthersSection = null;
        this.subthemeOthersSection = null;
        this.maxCount = 1;
        const sub = this.getGroupedSubThemeDistribution(s);
        this.groupedSubThemes = sub.groups;
        this.subthemeOthersSection = sub.othersSection;
        this.maxSubThemeCount = this.maxSubThemeCountForSubthemeView(
          this.groupedSubThemes,
          this.subthemeOthersSection?.themeGroups ?? [],
        );
        /** Sub-theme-wise: every main theme group starts collapsed until the user expands it. */
        this.collapsedThemeIds = new Set(
          this.groupedSubThemes.map((g) => String(g.themeId ?? '').trim()).filter(Boolean),
        );
        /** Suggested-only theme groups inside Others start collapsed too. */
        this.collapsedSubthemeOthersInnerThemeIds = new Set(
          (this.subthemeOthersSection?.themeGroups ?? []).map((g) =>
            this.subthemeOthersInnerKey(String(g.themeId ?? '').trim()),
          ),
        );
      }
    } catch {
      this.distribution = [];
      this.themeMainRows = [];
      this.themeOthersSection = null;
      this.groupedSubThemes = [];
      this.subthemeOthersSection = null;
      this.collapsedThemeIds.clear();
      this.collapsedSubthemeOthersInnerThemeIds.clear();
      this.maxCount = 1;
      this.maxSubThemeCount = 1;
    }
  }

  private buildThemeSuggestedLookup(s: DashboardStats): Map<string, boolean> {
    const m = new Map<string, boolean>();
    for (const t of s.themeSentimentBreakdown ?? []) {
      if (!t?.themeId) continue;
      m.set(String(t.themeId), t.isSuggestedTheme === true);
    }
    return m;
  }

  private isThemeRowSuggested(
    themeId: string,
    explicit: boolean | undefined,
    lookup: Map<string, boolean>
  ): boolean {
    if (explicit === true) return true;
    return lookup.get(String(themeId)) === true;
  }

  private buildThemeDistributionView(s: DashboardStats): {
    mainRows: Array<{ id: string; name: string; count: number }>;
    othersSection: { total: number; rows: Array<{ id: string; name: string; count: number }> } | null;
    maxBarCount: number;
  } {
    const breakdown = (s.themeSentimentBreakdown ?? []) as ThemeSentimentBreakdown[];
    const suggestedLookup = this.buildThemeSuggestedLookup(s);

    type Row = { id: string; name: string; count: number; isSuggested: boolean };
    let rows: Row[] = [];

    if (this.sentimentFilter === 'all') {
      if (s.themeDistribution?.length) {
        rows = s.themeDistribution.map((t) => ({
          id: t.themeId,
          name: t.themeName,
          count: t.count,
          isSuggested: this.isThemeRowSuggested(t.themeId, t.isSuggestedTheme, suggestedLookup),
        }));
      } else {
        rows = breakdown.map((t) => ({
          id: t.themeId,
          name: t.themeName,
          count:
            (t.sentiment?.positive?.count ?? 0) +
            (t.sentiment?.neutral?.count ?? 0) +
            (t.sentiment?.negative?.count ?? 0),
          isSuggested: t.isSuggestedTheme === true,
        }));
      }
    } else {
      rows = breakdown
        .map((t) => ({
          id: t.themeId,
          name: t.themeName,
          count: t.sentiment?.[this.sentimentFilter]?.count ?? 0,
          isSuggested: t.isSuggestedTheme === true,
        }))
        .filter((r) => r.count > 0);
    }

    const regular = rows.filter((r) => !r.isSuggested);
    const suggested = rows.filter((r) => r.isSuggested);

    const byCountThenName = (a: Row, b: Row) => {
      const diff = b.count - a.count;
      if (diff !== 0) return diff;
      return String(a.name).localeCompare(String(b.name));
    };

    const mainRows = regular.slice().sort(byCountThenName).map(({ id, name, count }) => ({ id, name, count }));
    const othersRows = suggested
      .slice()
      .sort(byCountThenName)
      .map(({ id, name, count }) => ({ id, name, count }));
    const othersTotal = othersRows.reduce((sum, r) => sum + r.count, 0);
    const othersSection =
      othersRows.length > 0 ? { total: othersTotal, rows: othersRows } : null;

    const allCounts = [...mainRows.map((r) => r.count), ...othersRows.map((r) => r.count)];
    const maxBarCount = allCounts.length ? Math.max(...allCounts, 1) : 1;

    return { mainRows, othersSection, maxBarCount };
  }

  private getSubThemeCount(st: SubThemeSentimentBreakdown): number {
    if (!st?.sentiment) return 0;
    if (this.sentimentFilter !== 'all') {
      return st.sentiment?.[this.sentimentFilter]?.count ?? 0;
    }
    return (
      (st.sentiment.positive?.count ?? 0) +
      (st.sentiment.neutral?.count ?? 0) +
      (st.sentiment.negative?.count ?? 0)
    );
  }

  private maxSubThemeCountForSubthemeView(
    groups: GroupedSubThemeGroup[],
    othersThemeGroups: GroupedSubThemeGroup[],
  ): number {
    let m = 1;
    for (const g of groups) {
      for (const sub of g.subThemes) {
        if (sub.count > m) m = sub.count;
      }
      for (const sub of g.othersInTheme?.rows ?? []) {
        if (sub.count > m) m = sub.count;
      }
    }
    for (const g of othersThemeGroups) {
      for (const sub of g.subThemes) {
        if (sub.count > m) m = sub.count;
      }
    }
    return m;
  }

  private getGroupedSubThemeDistribution(s: DashboardStats): {
    groups: GroupedSubThemeGroup[];
    othersSection: { total: number; themeGroups: GroupedSubThemeGroup[] } | null;
  } {
    const breakdown = (s.subThemeSentimentBreakdown ?? []).filter(Boolean) as SubThemeSentimentBreakdown[];
    const themeNameById = new Map<string, string>();
    (s.themeSentimentBreakdown ?? []).filter(Boolean).forEach((t: { themeId?: string; themeName?: string }) => {
      if (t?.themeId) themeNameById.set(String(t.themeId), String(t.themeName ?? t.themeId));
    });
    (s.themeDistribution ?? []).filter(Boolean).forEach((t: { themeId?: string; themeName?: string }) => {
      if (t?.themeId) themeNameById.set(String(t.themeId), String(t.themeName ?? t.themeId));
    });

    type SubRow = SubThemeRow & { themeId: string; isSuggestedTheme: boolean; isSuggestedSubtheme: boolean };
    const list: SubRow[] = breakdown.map((st) => {
      const themeId = String(st?.themeId ?? '').trim() || 'unknown';
      const subThemeId = String(st?.subThemeId ?? '').trim() || 'unknown';
      const subThemeName = String(st?.subThemeName ?? '').trim() || 'Unknown';
      const count = this.getSubThemeCount(st);
      return {
        themeId,
        subThemeId,
        subThemeName,
        count,
        key: `${themeId}__${subThemeId}`,
        isSuggestedTheme: st.isSuggestedTheme === true,
        isSuggestedSubtheme: st.isSuggestedSubtheme === true,
      };
    });

    /** Outside global Others: parent theme is not LLM-suggested. */
    const outsideOthersList = list.filter((r) => !r.isSuggestedTheme);
    /** Bottom "Others": parent theme is LLM-suggested. */
    const globalOthersList = list.filter((r) => r.isSuggestedTheme);

    const groups = new Map<string, { themeId: string; themeName: string; total: number; rows: SubRow[] }>();
    for (const st of outsideOthersList) {
      const themeName = themeNameById.get(st.themeId) ?? st.themeId;
      if (!groups.has(st.themeId)) {
        groups.set(st.themeId, { themeId: st.themeId, themeName, total: 0, rows: [] });
      }
      const g = groups.get(st.themeId)!;
      g.rows.push(st);
      g.total += st.count;
    }

    const bySubCountThenName = (a: SubThemeRow, b: SubThemeRow) => {
      const diff = b.count - a.count;
      if (diff !== 0) return diff;
      return String(a.subThemeName).localeCompare(String(b.subThemeName));
    };

    const arr: GroupedSubThemeGroup[] = Array.from(groups.values())
      .map((g) => {
        const regular = g.rows.filter((r) => !r.isSuggestedSubtheme);
        const innerOthersRows = g.rows.filter((r) => r.isSuggestedSubtheme);
        const sortedRegular: SubThemeRow[] = regular
          .map(({ themeId: _tid, isSuggestedTheme: _th, isSuggestedSubtheme: _sg, ...row }) => row)
          .slice()
          .sort(bySubCountThenName);
        const sortedInnerOthers: SubThemeRow[] = innerOthersRows
          .map(({ themeId: _tid, isSuggestedTheme: _th, isSuggestedSubtheme: _sg, ...row }) => row)
          .slice()
          .sort(bySubCountThenName);
        const othersTotal = sortedInnerOthers.reduce((sum, r) => sum + r.count, 0);
        const othersInTheme =
          sortedInnerOthers.length > 0 ? { total: othersTotal, rows: sortedInnerOthers } : null;
        return {
          themeId: g.themeId,
          themeName: g.themeName,
          total: g.total,
          subThemes: sortedRegular,
          othersInTheme,
        };
      })
      .filter((g) => g.subThemes.length > 0 || g.othersInTheme != null)
      .sort((a, b) => {
        const diff = b.total - a.total;
        if (diff !== 0) return diff;
        return String(a.themeName).localeCompare(String(b.themeName));
      });

    const mainThemeIds = new Set(arr.map((g) => String(g.themeId ?? '').trim()).filter(Boolean));

    const othersMap = new Map<string, { themeId: string; themeName: string; total: number; rows: SubRow[] }>();
    for (const st of globalOthersList) {
      const themeName = themeNameById.get(st.themeId) ?? st.themeId;
      if (!othersMap.has(st.themeId)) {
        othersMap.set(st.themeId, { themeId: st.themeId, themeName, total: 0, rows: [] });
      }
      const og = othersMap.get(st.themeId)!;
      og.rows.push(st);
      og.total += st.count;
    }
    const themeGroups: GroupedSubThemeGroup[] = Array.from(othersMap.values())
      .map((g) => ({
        themeId: g.themeId,
        themeName: g.themeName,
        total: g.total,
        subThemes: g.rows
          .map(({ themeId: _tid, isSuggestedTheme: _th, isSuggestedSubtheme: _sg, ...row }) => row)
          .slice()
          .sort(bySubCountThenName),
        othersInTheme: null,
      }))
      .filter((g) => g.subThemes.length > 0)
      .filter((g) => !mainThemeIds.has(String(g.themeId ?? '').trim()))
      .sort((a, b) => {
        const diff = b.total - a.total;
        if (diff !== 0) return diff;
        return String(a.themeName).localeCompare(String(b.themeName));
      });
    const othersTotal = themeGroups.reduce((sum, g) => sum + g.total, 0);
    const othersSection =
      themeGroups.length > 0 ? { total: othersTotal, themeGroups } : null;

    return { groups: arr, othersSection };
  }

  private subthemeOthersInnerKey(themeId: string): string {
    return `others-inner::${String(themeId ?? '').trim()}`;
  }

  toggleSubthemeOthersInnerCollapsed(themeId: string): void {
    const raw = String(themeId ?? '').trim();
    if (!raw) return;
    const id = this.subthemeOthersInnerKey(raw);
    if (this.collapsedSubthemeOthersInnerThemeIds.has(id)) {
      this.collapsedSubthemeOthersInnerThemeIds.delete(id);
    } else {
      this.collapsedSubthemeOthersInnerThemeIds.add(id);
    }
  }

  isSubthemeOthersInnerCollapsed(themeId: string): boolean {
    return this.collapsedSubthemeOthersInnerThemeIds.has(this.subthemeOthersInnerKey(String(themeId ?? '').trim()));
  }

  trackBySubThemeRowKey(_index: number, sub: SubThemeRow): string {
    return sub.key;
  }

  toggleThemeCollapsed(themeId: string): void {
    const id = String(themeId ?? '').trim();
    if (!id) return;
    if (this.collapsedThemeIds.has(id)) this.collapsedThemeIds.delete(id);
    else this.collapsedThemeIds.add(id);
  }

  isThemeCollapsed(themeId: string): boolean {
    return this.collapsedThemeIds.has(String(themeId ?? '').trim());
  }

  toggleOthersSection(sectionId: string): void {
    const id = String(sectionId ?? '').trim();
    if (!id) return;
    if (this.othersSectionExpandedIds.has(id)) this.othersSectionExpandedIds.delete(id);
    else this.othersSectionExpandedIds.add(id);
  }

  isOthersSectionExpanded(sectionId: string): boolean {
    return this.othersSectionExpandedIds.has(String(sectionId ?? '').trim());
  }

  readonly themesOthersSectionId = THEMES_OTHERS_SECTION_ID;
  readonly subthemesOthersSectionId = SUBTHEMES_OTHERS_SECTION_ID;

  /** Expand/collapse for per-theme inline Others (sub-theme view). */
  subthemeInlineOthersSectionId(themeId: string): string {
    return `_inline_subthemes_others_::${String(themeId ?? '').trim()}`;
  }

  trackByThemeGroupId(_index: number, g: GroupedSubThemeGroup): string {
    return String(g.themeId ?? '').trim();
  }

  onSentimentChange(value: string): void {
    this.sentimentFilterChange.emit(value as SentimentFilterValue);
  }

  onViewModeChange(value: string): void {
    this.viewModeChange.emit(value as ViewModeValue);
  }
}
